import { type ApprovalRequestId } from "@cmmd-forge/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type SessionPhase, type Thread } from "../../types";
import {
  deriveNextLocalDispatchSnapshot,
  hasServerAcknowledgedLocalDispatch,
  type LocalDispatchSnapshot,
} from "../ChatView.logic";

export function useLocalDispatchState(input: {
  readonly activeThread: Thread | undefined;
  readonly activeLatestTurn: Thread["latestTurn"] | null;
  readonly phase: SessionPhase;
  readonly activePendingApproval: ApprovalRequestId | null;
  readonly activePendingUserInput: ApprovalRequestId | null;
  readonly threadError: string | null | undefined;
}): {
  readonly beginLocalDispatch: (options?: { readonly preparingWorktree?: boolean }) => void;
  readonly resetLocalDispatch: () => void;
  readonly localDispatchStartedAt: string | null;
  readonly isPreparingWorktree: boolean;
  readonly isSendBusy: boolean;
} {
  const [localDispatch, setLocalDispatch] = useState<LocalDispatchSnapshot | null>(null);

  const beginLocalDispatch = useCallback(
    (options?: { readonly preparingWorktree?: boolean }) => {
      setLocalDispatch((current) =>
        deriveNextLocalDispatchSnapshot({
          current,
          activeThread: input.activeThread,
          options,
        }),
      );
    },
    [input.activeThread],
  );

  const resetLocalDispatch = useCallback(() => {
    setLocalDispatch(null);
  }, []);

  const serverAcknowledgedLocalDispatch = useMemo(
    () =>
      hasServerAcknowledgedLocalDispatch({
        localDispatch,
        phase: input.phase,
        latestTurn: input.activeLatestTurn,
        session: input.activeThread?.session ?? null,
        hasPendingApproval: input.activePendingApproval !== null,
        hasPendingUserInput: input.activePendingUserInput !== null,
        threadError: input.threadError,
      }),
    [
      input.activeLatestTurn,
      input.activePendingApproval,
      input.activePendingUserInput,
      input.activeThread?.session,
      input.phase,
      input.threadError,
      localDispatch,
    ],
  );

  useEffect(() => {
    if (!serverAcknowledgedLocalDispatch) {
      return;
    }
    resetLocalDispatch();
  }, [resetLocalDispatch, serverAcknowledgedLocalDispatch]);

  return {
    beginLocalDispatch,
    resetLocalDispatch,
    localDispatchStartedAt: localDispatch?.startedAt ?? null,
    isPreparingWorktree: localDispatch?.preparingWorktree ?? false,
    isSendBusy: localDispatch !== null && !serverAcknowledgedLocalDispatch,
  };
}
